import { useState, useEffect } from "react";
import { SHOW_COSTING } from "@/lib/features";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Sparkles, UploadCloud, Loader2, AlertTriangle, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import { API, api } from "@/lib/api";
import { logger } from "@/lib/logger";

const yellow = "border-yellow-400 bg-yellow-50 focus-visible:ring-yellow-300";

function normDate(v) {
  if (!v) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
  if (m) return m[0];
  const d = new Date(v);
  return isNaN(d) ? "" : d.toISOString().slice(0, 10);
}

export default function AiPoImportDialog({ onClose, customers = [], finishedGoods = [], onCreate }) {
  const [step, setStep] = useState("upload");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isPdf, setIsPdf] = useState(false);
  const [currency, setCurrency] = useState("");
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const [custUncertain, setCustUncertain] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [poNumUncertain, setPoNumUncertain] = useState(false);
  const [poDate, setPoDate] = useState("");
  const [poDateUncertain, setPoDateUncertain] = useState(false);
  const [rows, setRows] = useState([]);

  const extract = async () => {
    if (!file) return;
    setBusy(true);
    try {
      try { const url = URL.createObjectURL(file); setPreviewUrl(url); setIsPdf((file.type || "").includes("pdf")); } catch (e) { logger.error("PO preview failed:", e); }
      const fd = new FormData();
      fd.append("file", file);
      const csrf = (document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/) || [])[1] || "";
      const res = await fetch(`${API}/sales-orders/ai-extract`, { method: "POST", credentials: "include", headers: { "X-CSRF-Token": decodeURIComponent(csrf) }, body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data?.detail === "string" ? data.detail : "Extraction failed");
      setCustUncertain(!!data.customer?.uncertain);
      setCustomerId(data.customer?.suggested_customer_id || "");
      setPoNumber(data.po_number?.value || "");
      setPoNumUncertain(!!data.po_number?.uncertain);
      setPoDate(normDate(data.po_date?.value));
      setPoDateUncertain(!!data.po_date?.uncertain);
      setCurrency(data.currency?.value || "");
      setRows((data.line_items || []).map((li, i) => ({
        key: `r${i}`,
        item_id: li.item_id || "",
        quantity: li.quantity != null ? String(li.quantity) : "",
        unit_price: li.unit_price != null ? String(li.unit_price) : "",
        uncertain: !!li.uncertain,
        raw_sku: li.raw_sku || "",
        description: li.description || "",
        source: li.source || null,
        detected_unit: li.detected_unit || "",
      })));
      setStep("review");
      toast.success("Purchase order read — please review the highlighted fields");
    } catch (e) {
      toast.error(e.message || "Could not read the document");
    } finally {
      setBusy(false);
    }
  };

  const setRow = (i, patch) => setRows((p) => p.map((r, idx) => (idx === i ? { ...r, ...patch, uncertain: false } : r)));
  const removeRow = (i) => setRows((p) => p.filter((_, idx) => idx !== i));

  const valid = customerId && rows.length > 0 && rows.every((r) => r.item_id && Number(r.quantity) > 0);

  const create = async () => {
    setSaving(true);
    try {
      const items = rows
        .filter((r) => r.item_id && Number(r.quantity) > 0)
        .map((r) => {
          const fg = finishedGoods.find((f) => f.id === r.item_id);
          return { item_id: fg.id, sku: fg.sku, name: fg.name, unit: fg.unit || "pcs", quantity: Number(r.quantity), unit_price: Number(r.unit_price) || 0 };
        });
      await onCreate({
        customer_id: customerId,
        items,
        customer_po_number: poNumber || null,
        po_date: poDate || null,
        required_delivery_date: null,
        notes: "Imported via AI PO Import",
      });
      // Learn this customer's SKU naming for future orders (cross-reference).
      if (customerId) {
        const mappings = rows
          .filter((r) => r.item_id && (r.raw_sku || "").trim())
          .map((r) => ({ customer_id: customerId, external_sku: r.raw_sku.trim(), item_id: r.item_id }));
        if (mappings.length) { try { await api.post("/customer-part-maps", { mappings }); } catch (e) { logger.error("Saving customer part mappings failed:", e); } }
      }
      onClose();
    } catch (e) {
      logger.error("AI PO import create failed (toast shown by onCreate):", e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={`${step === "review" ? "max-w-5xl" : "max-w-2xl"} max-h-[88vh] overflow-y-auto`} data-testid="ai-po-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Sparkles className="h-4 w-4 text-indigo-600" /> AI PO Import</DialogTitle>
          <DialogDescription className="text-xs">
            {step === "upload"
              ? "Upload a customer purchase order (PDF or image). AI will extract the details for you to review."
              : "Review the extracted data. Fields highlighted in yellow were uncertain — please double-check before saving."}
          </DialogDescription>
        </DialogHeader>

        {step === "upload" ? (
          <div className="mt-2 space-y-3">
            <label
              htmlFor="ai-po-file"
              className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 hover:border-indigo-400 hover:bg-indigo-50/40 transition-colors py-10 cursor-pointer"
              data-testid="ai-po-dropzone"
            >
              {file ? <FileText className="h-8 w-8 text-indigo-500" /> : <UploadCloud className="h-8 w-8 text-slate-400" />}
              <span className="text-sm font-medium text-slate-700">{file ? file.name : "Click to choose a PDF or image"}</span>
              <span className="text-[11px] text-slate-400">PDF, PNG, JPG or WEBP · up to 15 MB</span>
              <input
                id="ai-po-file"
                type="file"
                accept=".pdf,image/png,image/jpeg,image/webp,image/heic,image/heif"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="ai-po-file-input"
              />
            </label>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button className="bg-indigo-600 hover:bg-indigo-700" disabled={!file || busy} onClick={extract} data-testid="ai-po-extract-btn">
                {busy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Reading…</> : <><Sparkles className="h-4 w-4 mr-1" /> Extract with AI</>}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="mt-1 space-y-4" data-testid="ai-po-review">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="hidden md:block border rounded-lg overflow-hidden bg-slate-100 h-[60vh]" data-testid="ai-po-preview">
                {previewUrl ? (isPdf
                  ? <iframe title="Original PO" src={previewUrl} className="w-full h-full" />
                  : <img src={previewUrl} alt="Original PO" className="w-full h-full object-contain" />)
                  : <div className="flex items-center justify-center h-full text-xs text-slate-400">Preview unavailable</div>}
              </div>
              <div className="space-y-4 md:max-h-[60vh] md:overflow-y-auto md:pr-1">
                <div className="flex items-center justify-between gap-2 rounded-lg bg-yellow-50 border border-yellow-200 px-3 py-1.5">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-3.5 w-3.5 text-yellow-600 shrink-0" />
                    <span className="text-[11px] text-yellow-800">Yellow = double-check before saving.</span>
                  </div>
                  {currency && <span className="text-[10px] font-semibold text-slate-600 bg-white border rounded px-2 py-0.5" data-testid="ai-po-currency">Currency: {currency}</span>}
                </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Customer *</Label>
                <SearchableSelect
                  value={customerId}
                  onValueChange={(v) => { setCustomerId(v); setCustUncertain(false); }}
                  placeholder="Select a customer"
                  searchPlaceholder="Search customers…"
                  options={customers.filter((c) => c.active !== false).map((c) => ({ value: c.id, label: `${c.name} · ${c.code}`, keywords: `${c.name} ${c.code}` }))}
                  triggerClassName={custUncertain ? yellow : ""}
                  testid="ai-po-customer"
                />
                {custUncertain && <p className="text-[10px] text-yellow-700">Double-check: the customer name on the PO didn't clearly match an existing customer.</p>}
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Customer PO Number</Label>
                <Input value={poNumber} onChange={(e) => { setPoNumber(e.target.value); setPoNumUncertain(false); }} className={poNumUncertain ? yellow : ""} data-testid="ai-po-number" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">PO Date</Label>
                <Input type="date" value={poDate} onChange={(e) => { setPoDate(e.target.value); setPoDateUncertain(false); }} className={poDateUncertain ? yellow : ""} data-testid="ai-po-date" />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Line Items *</Label>
              {rows.length === 0 && <p className="text-[11px] text-slate-400">No line items were detected. Add one from the Sales Order form instead.</p>}
              {rows.map((r, i) => (
                <div key={r.key} className={`rounded-lg border p-2 ${r.uncertain ? "border-yellow-400 bg-yellow-50" : "border-slate-200"}`} data-testid={`ai-po-row-${i}`}>
                  <div className="flex items-end gap-2">
                    <div className="flex-1 space-y-1">
                      <SearchableSelect
                        value={r.item_id}
                        onValueChange={(v) => setRow(i, { item_id: v })}
                        placeholder="Pick finished good"
                        searchPlaceholder="Search products…"
                        options={finishedGoods.map((f) => ({ value: f.id, label: `${f.sku} · ${f.name}`, keywords: `${f.sku} ${f.name}` }))}
                        triggerClassName={r.uncertain && !r.item_id ? yellow : ""}
                        testid={`ai-po-item-${i}`}
                      />
                    </div>
                    <div className="w-24 space-y-1">
                      <Input type="number" min="1" placeholder="Qty" value={r.quantity} onChange={(e) => setRow(i, { quantity: e.target.value })} data-testid={`ai-po-qty-${i}`} />
                    </div>
                    {SHOW_COSTING && (
                      <>
                        <div className="w-28 space-y-1">
                          <Input type="number" min="0" step="0.01" placeholder="Unit ₹" value={r.unit_price} onChange={(e) => setRow(i, { unit_price: e.target.value })} data-testid={`ai-po-price-${i}`} />
                        </div>
                        <div className="w-24 text-right text-[11px] text-slate-600 font-mono-data pb-2 shrink-0" data-testid={`ai-po-line-total-${i}`}>{(Number(r.quantity) || 0) * (Number(r.unit_price) || 0) > 0 ? `₹${((Number(r.quantity) || 0) * (Number(r.unit_price) || 0)).toLocaleString("en-IN")}` : "—"}</div>
                      </>
                    )}
                    <Button variant="ghost" size="icon" className="h-9 w-9 text-red-500" onClick={() => removeRow(i)} data-testid={`ai-po-row-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  {(r.raw_sku || r.description) && (
                    <p className="text-[10px] text-slate-500 mt-1 font-mono-data">
                      AI read: {[r.raw_sku, r.description].filter(Boolean).join(" — ")}{r.detected_unit ? ` · ${r.detected_unit}` : ""}
                      {r.source === "mapping" ? <span className="text-emerald-600 font-semibold"> · learned mapping ✓</span> : r.source === "catalog" ? <span className="text-slate-400"> · catalog match</span> : ""}
                    </p>
                  )}
                </div>
              ))}
            </div>
            </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep("upload")}>Back</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" disabled={!valid || saving} onClick={create} data-testid="ai-po-create-btn">
                {saving ? "Creating…" : "Create Sales Order"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
